import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { ROUTES, getRouteWithParams } from "~/utils";
import * as APITypes from "~/API";
import { apiClient, OrganizationRedirectLoaderResult } from "~/services/apiClient";

/**
 * Redirect loader that takes user to their organization details page
 * Route: /organization -> /organization/:organizationID
 *
 * Behavior:
 * - If user has no organization: redirect to create organization page
 * - If user is SuperAdmin or Admin: redirect to organization details page
 * - Otherwise (Manager, Accountant, MaintenanceStaff): redirect to their own member details page
 */
export async function OrganizationRedirectLoader({
  request,
}: LoaderFunctionArgs): Promise<Response> {
  const auth = getAuth();
  const user = await getReadyAuthUser();

  if (!user) {
    return redirect("/explore");
  }

  const result = await apiClient.loader<OrganizationRedirectLoaderResult>(
    "organization-redirect",
    { userID: user.uid }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load organization redirect:", result.error);
    throw new Error(
      result.error ?? "Failed to load organization redirect"
    );
  }

  const { organizationID, roles } = result.data;

  if (!organizationID) {
    return redirect(ROUTES.newOrganization);
  }

  if (
    roles === APITypes.ManagerRolesType.SuperAdmin ||
    roles === APITypes.ManagerRolesType.Admin
  ) {
    const organizationDetailsPath = getRouteWithParams("organizationDetails", {
      organizationID,
    });
    return redirect(organizationDetailsPath);
  }

  return redirect(`/organization/${organizationID}/member/${user.uid}`);
}
